/**
 * ContractsListPage · v2.7.35 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Classification-scoped listing page with two-tab Active / Inactive grid.
 * Route: /admin/contracts/:classificationSlug
 *
 * - REVOCATION INTERFACE: an explicit toggle on each row flips active state.
 * - Click row → /admin/contracts/:slug/:contract_id (detail page).
 */
import { useCallback, useEffect, useMemo, useState, memo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Briefcase, Plus, Search, ExternalLink, Power, Building2, Crown, Truck, FlagTriangleRight, Landmark, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { contractsApi, CLASSIFICATION_LABELS, URL_SLUG_TO_CLASSIFICATION } from "@/lib/contractsApi";

const CLASS_ICONS = {
  personal: UserIcon, business: Building2, municipal: Landmark, state: FlagTriangleRight,
  federal: Crown, dot: Truck,
};

export default function ContractsListPage() {
  const { classificationSlug } = useParams();
  const classification = URL_SLUG_TO_CLASSIFICATION[classificationSlug];
  const label = CLASSIFICATION_LABELS[classification] || "Contracts";
  const Icon = CLASS_ICONS[classification] || Briefcase;
  const nav = useNavigate();

  const [activeTab, setActiveTab] = useState("active");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!classification) return;
    try {
      const { data } = await contractsApi.list({ classification, active: activeTab === "active" });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  }, [classification, activeTab]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((c) =>
      [c.id, c.business_name, c.contact_first_name, c.contact_last_name, c.primary_office_phone, c.mobile_phone]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  const onToggle = async (c) => {
    setBusy(true);
    try {
      const { data } = await contractsApi.toggleActive(c.id);
      toast.success(`${c.id} → ${data.active ? "ACTIVE" : "INACTIVE"}`);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Toggle failed";
      if (detail.includes("Insufficient")) toast.error(detail);
      else toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!classification) {
    return (
      <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-8" data-testid="contracts-list-page-invalid">
        <p className="text-rose-300">Invalid classification: {classificationSlug}</p>
        <Link to="/admin" className="text-amber-400 underline">← Admin Console</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid={`contracts-list-${classification}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider" data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4"/>Admin Console
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <Icon className="h-6 w-6 text-emerald-300"/>
            <h1 className="text-2xl font-extrabold tracking-tight">{label} Contracts</h1>
            <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-400/40 text-[10px] uppercase tracking-widest">Contracts module</Badge>
          </div>
          <Button asChild className="bg-emerald-500 text-zinc-900 hover:bg-emerald-400 font-bold" data-testid="contracts-new-btn">
            <Link to="/admin/contracts/new"><Plus className="h-4 w-4 mr-1.5"/>New Contract</Link>
          </Button>
        </div>

        {/* Tab strip · Active / Inactive */}
        <div className="flex items-center gap-2 border-b border-zinc-800 pb-0">
          {["active", "inactive"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`contracts-tab-${tab}`}
              className={`px-4 py-2 text-xs font-extrabold uppercase tracking-widest transition-colors border-b-2 -mb-[2px] ${
                activeTab === tab
                  ? "text-emerald-200 border-b-emerald-400"
                  : "text-zinc-400 hover:text-zinc-200 border-b-transparent"
              }`}
            >
              {tab === "active" ? "Active Accounts" : "Inactive Accounts"}
            </button>
          ))}
          <div className="ml-auto relative w-72">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"/>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ID, name, contact, phone…" className="bg-zinc-950 border-zinc-700 pl-8 h-8 text-xs" data-testid="contracts-search-input"/>
          </div>
        </div>

        {/* Grid */}
        <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden">
          <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-zinc-400 font-bold border-b border-zinc-800 px-3 py-2 bg-zinc-950">
            <div className="col-span-3">Contract ID</div>
            <div className="col-span-3">Business Name</div>
            <div className="col-span-2">Contact</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-1 text-right">Rate</div>
            <div className="col-span-1 text-right">State</div>
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-12 text-center text-zinc-500 text-sm" data-testid="contracts-list-empty">
              No {activeTab} {label.toLowerCase()} contracts.
              {activeTab === "active" && (
                <div className="mt-2"><Link to="/admin/contracts/new" className="text-emerald-300 hover:text-emerald-200 underline text-xs">+ Onboard your first {label.toLowerCase()} contract</Link></div>
              )}
            </div>
          ) : (
            filtered.map((c) => (
              <ContractRow
                key={c.id}
                contract={c}
                classificationSlug={classificationSlug}
                busy={busy}
                onNavigate={() => nav(`/admin/contracts/${classificationSlug}/${c.id}`)}
                onToggle={() => onToggle(c)}
              />
            ))
          )}
        </Card>

        <p className="text-[10px] text-zinc-500 italic">
          <Power className="h-3 w-3 inline mr-1"/>
          Switching a contract to <span className="text-zinc-300 font-bold">Inactive</span> immediately invalidates its booking privileges and charge codes across the dispatch system. Toggle back to restore access. Action is audit-logged.
        </p>
      </div>
    </div>
  );
}

// Memoized row · prevents re-render of all 1000+ contract rows when one
// toggle flips or a sibling unrelated state changes. Significantly speeds
// up dense lists at 1080p/4K without needing react-window virtualization.
const ContractRow = memo(function ContractRow({ contract, classificationSlug, busy, onNavigate, onToggle }) {
  const c = contract;
  return (
    <div
      className="grid grid-cols-12 items-center px-3 py-2.5 text-xs border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900/60 transition-colors cursor-pointer"
      onClick={(e) => {
        if (e.target.closest("[data-stop-row-nav]")) return;
        onNavigate();
      }}
      data-testid={`contract-row-${c.id}`}
    >
      <div className="col-span-3 font-mono text-emerald-300 font-bold">{c.id}</div>
      <div className="col-span-3 text-zinc-100">{c.business_name}</div>
      <div className="col-span-2 text-zinc-300">{c.contact_first_name} {c.contact_last_name}</div>
      <div className="col-span-2 text-zinc-400 font-mono text-[11px]">{c.primary_office_phone || c.mobile_phone || "—"}</div>
      <div className="col-span-1 text-right text-zinc-300">{(c.rate_multiplier ?? 1).toFixed(2)}×</div>
      <div className="col-span-1 flex items-center justify-end gap-2" data-stop-row-nav>
        <Switch
          checked={!!c.active}
          onCheckedChange={onToggle}
          disabled={busy}
          data-testid={`contract-toggle-${c.id}`}
          title={c.active ? "Click to deactivate (revoke booking privileges)" : "Click to reactivate"}
        />
        <Link to={`/admin/contracts/${classificationSlug}/${c.id}`} className="text-zinc-500 hover:text-emerald-300" title="Open detail" onClick={(e) => e.stopPropagation()}>
          <ExternalLink className="h-3.5 w-3.5"/>
        </Link>
      </div>
    </div>
  );
});
